import React, { useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { cx } from '@emotion/css';
import styles from './FlyerDetailsBottom.styles';
import { ButtonPrimaryLarge } from '@/design-system';
import { CallToAction } from '@/graphql/types';
import { CTAMapper } from '@/pages/FlyerDetails/CtaMapper';
import getValidHref from '@/utils/getValidHref';
import { useGoogleTranslator } from '@/hooks';

interface Props {
  ctas: CallToAction[];
  trackCTAButton?: (
    buttonName: string,
    link: string,
    type: string,
    featureId?: string | null
  ) => void;
}

export interface FlyerDetailsBottomRef {
  focusCTA: () => void;
}

const CTAButton = React.memo(
  forwardRef<
    HTMLButtonElement,
    {
      label: string;
      icon: React.ElementType;
      onClick: () => void;
    }
  >(({ label, icon: Icon, onClick }, ref) => {
    const labelTranslated = useGoogleTranslator(label);

    return (
      <>
        <span className="sr-only">{labelTranslated}</span>
        <ButtonPrimaryLarge
          ref={ref}
          onClick={onClick}
          data-testid="CTAButton"
          fullWidth
          nonTranslate
          role="link"
          tabIndex={1}
        >
          <Icon />
          {labelTranslated}
        </ButtonPrimaryLarge>
      </>
    );
  })
);

export const FlyerDetailsBottom = React.memo(
  forwardRef<FlyerDetailsBottomRef, Props>(({ ctas, trackCTAButton }, ref) => {
    const ctaButtonRef = useRef<HTMLButtonElement>(null);

    // Primary CTA is first in array
    const primaryCTA = useMemo(() => ctas[0], [ctas]);

    useImperativeHandle(ref, () => ({
      focusCTA: () => {
        if (ctaButtonRef.current) {
          ctaButtonRef.current.focus();
        }
      },
    }));

    const renderPrimaryCTA = () => {
      if (!primaryCTA) return null;
      const { type, value, id } = primaryCTA;
      const ctaButton = CTAMapper[type];
      if (!ctaButton) return null;

      return (
        <CTAButton
          ref={ctaButtonRef}
          label={ctaButton.label}
          icon={ctaButton.icon}
          onClick={() => {
            if (trackCTAButton) trackCTAButton(ctaButton.label, value, type, id);
            const link = getValidHref(value, type);
            const target = ['email', 'call'].includes(type) ? '_self' : '_blank';
            window.open(link, target);
          }}
        />
      );
    };

    const primaryCtaButton = renderPrimaryCTA();

    if (!primaryCtaButton) return null;

    return <div className={styles.bottomContainer}>{primaryCtaButton}</div>;
  })
);
